if (!customElements.get('chillout-hero')) {
  class ChilloutHero extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-hero-track]');
      this.slides = [...this.querySelectorAll('[data-hero-slide]')];
      this.dots = [...this.querySelectorAll('[data-hero-dot]')];
      this.videos = [...this.querySelectorAll('video')];
      if (!this.track || !this.slides.length) return;

      this.index = 0;
      this.hovered = false;
      this.editing = false;
      this.visible = false;
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.events = new AbortController();
      const listen = (target, event, callback) => {
        target?.addEventListener(event, callback, { signal: this.events.signal });
      };

      listen(this.querySelector('[data-hero-next]'), 'click', () => this.go(this.index + 1));
      listen(this.querySelector('[data-hero-prev]'), 'click', () => this.go(this.index - 1));
      this.dots.forEach((dot, index) => listen(dot, 'click', () => this.go(index)));
      listen(this.track, 'scroll', () => {
        clearTimeout(this.scrolling);
        this.scrolling = setTimeout(() => {
          if (!this.track.clientWidth) return;
          this.index = Math.max(0, Math.min(this.slides.length - 1,
            Math.round(this.track.scrollLeft / this.track.clientWidth)));
          this.syncSlides();
        }, 120);
      });
      listen(this, 'mouseenter', () => { this.hovered = true; this.stop(); });
      listen(this, 'mouseleave', () => { this.hovered = false; this.start(); });
      listen(this.mobile, 'change', () => this.syncVideos());
      listen(this.reducedMotion, 'change', () => { this.syncVideos(); this.start(); });
      listen(document, 'visibilitychange', () => { this.syncVideos(); this.start(); });
      listen(this, 'shopify:block:select', (event) => {
        const index = this.slides.indexOf(event.target.closest('[data-hero-slide]'));
        if (index < 0) return;
        this.editing = true;
        this.go(index, 'instant');
      });
      listen(this, 'shopify:block:deselect', () => { this.editing = false; this.start(); });

      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.syncVideos();
        this.start();
      });
      this.observer.observe(this);
      this.syncSlides();
    }

    disconnectedCallback() {
      this.stop();
      clearTimeout(this.scrolling);
      this.events?.abort();
      this.observer?.disconnect();
      this.videos?.forEach((video) => video.pause());
    }

    go(index, behavior = 'smooth') {
      this.index = (index + this.slides.length) % this.slides.length;
      this.track.scrollTo({
        left: this.index * this.track.clientWidth,
        behavior: this.reducedMotion.matches ? 'instant' : behavior,
      });
      this.syncSlides();
      this.start();
    }

    syncSlides() {
      this.dots.forEach((dot, index) => {
        dot.classList.toggle('is-active', index === this.index);
        dot.setAttribute('aria-current', index === this.index ? 'true' : 'false');
      });
      this.syncVideos();
    }

    syncVideos() {
      this.videos.forEach((video) => {
        const correctViewport = this.mobile.matches
          ? !video.classList.contains('chillout-hero__video--desktop')
          : !video.classList.contains('chillout-hero__video--mobile');
        const shouldPlay = this.visible && !document.hidden && !this.reducedMotion.matches
          && correctViewport && this.slides[this.index].contains(video);
        if (shouldPlay) {
          video.muted = true;
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    }

    start() {
      this.stop();
      const delay = Number(this.dataset.delay);
      if (this.dataset.autoplay === 'true' && this.slides.length > 1 && delay > 0
        && this.visible && !document.hidden && !this.reducedMotion.matches
        && !this.hovered && !this.editing) {
        this.timer = setInterval(() => this.go(this.index + 1), delay);
      }
    }

    stop() {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  customElements.define('chillout-hero', ChilloutHero);
}
